import { Box, Tooltip, tooltipClasses } from '@mui/material';
import { Info } from 'lucide-react';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    LabelText,
    LabelValueBox,
    StyledIdField,
    ValueText,
} from '~/components/Fields/TooltipLabeledValue';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

type ImageInfoProps = {
    image: Image3D;
};

export default function ImageInfo({ image }: ImageInfoProps) {
    const {
        frameOfReferenceUid,
        modality,
        length,
        series,
        rows,
        cols,
        manufacturer,
        manufacturerModelName,
        modalitySpecificAttributes,
        pixelSpacing,
        imageOrientation,
        patientPosition,
        stationName,
        filterType,
    } = transform3DImageData(image);
    return (
        <Tooltip
            PopperProps={{
                sx: {
                    [`& .${tooltipClasses.tooltip}`]: {
                        maxWidth: 'none',
                    },
                },
            }}
            title={
                <Box whiteSpace="nowrap" overflow="hidden">
                    <LabelValueBox>
                        <LabelText>Modality:</LabelText>
                        <ValueText>{modality}</ValueText>
                    </LabelValueBox>
                    <LabelValueBox>
                        <LabelText>Slices:</LabelText>
                        <ValueText>{length}</ValueText>
                    </LabelValueBox>
                    <Box display="flex" gap={2}>
                        <LabelValueBox>
                            <LabelText>Rows:</LabelText>
                            <ValueText>{rows}</ValueText>
                        </LabelValueBox>
                        <LabelValueBox>
                            <LabelText>Columns:</LabelText>
                            <ValueText>{cols}</ValueText>
                        </LabelValueBox>
                    </Box>
                    <LabelValueBox>
                        <LabelText>Series Date:</LabelText>
                        <ValueText>
                            {series?.date?.toDateString() ?? 'N/A'}
                        </ValueText>
                    </LabelValueBox>
                    <LabelValueBox>
                        <LabelText>Series Description:</LabelText>
                        <ValueText>{series.description}</ValueText>
                    </LabelValueBox>

                    <StyledIdField label="Series Instance UID" id={series.id} />
                    <StyledIdField
                        label="Frame of Reference UID"
                        id={frameOfReferenceUid}
                    />

                    <LabelValueBox>
                        <LabelText>
                            <FormattedMessage id="manufacturer" />:
                        </LabelText>
                        <ValueText>{manufacturer}</ValueText>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>
                            <FormattedMessage id="manufacturerModelName" />:
                        </LabelText>
                        <ValueText>{manufacturerModelName}</ValueText>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>Pixel Spacing(mm):</LabelText>
                        <ValueText>
                            {pixelSpacing?.map((x) => x.toFixed(3)).join(' x ')}
                        </ValueText>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>Image Orientation:</LabelText>
                        <Box display="flex" gap={0.5}>
                            {imageOrientation?.map((axis, index) => (
                                <ValueText key={index}>
                                    [
                                    {axis
                                        .map((coord) =>
                                            Math.abs(coord) == 1 || coord == 0
                                                ? coord
                                                : coord.toFixed(2)
                                        )
                                        .join(',')}
                                    ]
                                </ValueText>
                            ))}
                        </Box>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>Patient Position:</LabelText>
                        <ValueText>{patientPosition}</ValueText>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>Station Name:</LabelText>
                        <ValueText>{stationName}</ValueText>
                    </LabelValueBox>

                    <LabelValueBox>
                        <LabelText>Filter Type:</LabelText>
                        <ValueText>{filterType}</ValueText>
                    </LabelValueBox>

                    {modality === Modality.CT && (
                        <Fragment>
                            <LabelValueBox>
                                <LabelText>KVP:</LabelText>
                                <ValueText>
                                    {modalitySpecificAttributes?.kvp}
                                </ValueText>
                            </LabelValueBox>

                            <LabelValueBox>
                                <LabelText>CTDI:</LabelText>
                                <ValueText>
                                    {modalitySpecificAttributes?.ctdi}
                                </ValueText>
                            </LabelValueBox>

                            <LabelValueBox>
                                <LabelText>Convolution Kernel:</LabelText>
                                <ValueText>
                                    {modalitySpecificAttributes?.kernel}
                                </ValueText>
                            </LabelValueBox>

                            <LabelValueBox>
                                <LabelText>Protocol Name:</LabelText>
                                <ValueText>
                                    {modalitySpecificAttributes?.protocolName}
                                </ValueText>
                            </LabelValueBox>
                        </Fragment>
                    )}

                    {modality === Modality.MR && (
                        <Fragment>
                            <LabelValueBox>
                                <LabelText>Scanning Sequence:</LabelText>
                                <ValueText>
                                    {
                                        modalitySpecificAttributes?.scanningSequence
                                    }
                                </ValueText>
                            </LabelValueBox>

                            <LabelValueBox>
                                <LabelText>MR Acquisition Type:</LabelText>
                                <ValueText>
                                    {
                                        modalitySpecificAttributes?.mraAcquisitionType
                                    }
                                </ValueText>
                            </LabelValueBox>

                            <LabelValueBox>
                                <LabelText>Sequence Variant:</LabelText>
                                <ValueText>
                                    {
                                        modalitySpecificAttributes?.sequenceVariant
                                    }
                                </ValueText>
                            </LabelValueBox>
                        </Fragment>
                    )}
                </Box>
            }
            placement="right-start"
        >
            <Info size={12} />
        </Tooltip>
    );
}
